import React from 'react';
import { Box, CircularProgress, Icon, IconButton, Tooltip } from '@material-ui/core';
import { DateFormatter, FileManager, PassedState } from 'components/AppTable/CellsComponents';
import i18next from 'i18next';
import { formatTrainingModuleLabel, formatTrainingPartLabel } from './formatTrainingLabels';

const incompleteLabel = () => i18next.t('trainingSessions.incomplete');

const formatEvidenceCell = (evidenceParams, helpers) => {
  if (!evidenceParams || typeof evidenceParams !== 'object') {
    return incompleteLabel();
  }

  const { hasCertificate, hasAssessmentResult, isPerformedWithSuccess, certificate, rowId, isMoodleCourse } =
    evidenceParams;

  if (isPerformedWithSuccess === false) {
    return incompleteLabel();
  }

  if (!hasCertificate && !hasAssessmentResult && isPerformedWithSuccess !== true) {
    return incompleteLabel();
  }

  const canLazyMoodleCertificate = Boolean(isMoodleCourse) && isPerformedWithSuccess === true;
  const showCertificateUpload = !isMoodleCourse && isPerformedWithSuccess === true && !hasCertificate;
  const showCertificateDownload = hasCertificate || canLazyMoodleCertificate;
  const showQuizAnswers = Boolean(hasAssessmentResult);
  const isCertificateDownloading = helpers.isDownloadingCertificate?.(rowId);
  const isAssessmentDownloading = helpers.isDownloadingAssessment?.(rowId);

  if (!showCertificateUpload && !showCertificateDownload && !showQuizAnswers) {
    return incompleteLabel();
  }

  return (
    <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
      {showCertificateDownload || showCertificateUpload ? (
        <FileManager
          value={{
            id: rowId,
            certificate: hasCertificate ? certificate || {} : canLazyMoodleCertificate ? {} : null,
            isPerformedWithSuccess,
            isMoodleCourse,
            isDownloading: Boolean(isCertificateDownloading),
          }}
          id={rowId}
          onChange={helpers.handleAddCertificate}
          onShowCertificate={helpers.handleShowCertificate}
        />
      ) : null}
      {showQuizAnswers ? (
        isAssessmentDownloading ? (
          <CircularProgress size={18} />
        ) : (
          <Tooltip title={i18next.t('trainingSessions.answersDownloadTooltip')} placement="top" arrow>
            <IconButton size="small" color="primary" onClick={() => helpers.handleDownloadQuizAnswers(rowId)}>
              <Icon fontSize="small">get_app</Icon>
            </IconButton>
          </Tooltip>
        )
      ) : null}
    </Box>
  );
};

export const getSessionsInProgressConfig = helpers => [
  {
    title: i18next.t('tablesLabels.id'),
    field: 'id',
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.trainingModule'),
    field: 'trainingModuleLabel',
  },
  {
    title: i18next.t('tablesLabels.trainingPart'),
    field: 'trainingPartLabel',
  },
  {
    title: i18next.t('tablesLabels.expiryDate'),
    field: 'expirationDate',
    Component: value => (value ? <DateFormatter value={value} /> : i18next.t('courseDetails.incomplete')),
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.jobTask'),
    field: 'jobTask',
    isSorting: true,
  },
  {
    title: '',
    field: 'isPerformed',
    dependentField: 'isMoodleSession',
    Component: (value, isMoodleSession) => (
      <PassedState onChange={helpers.handleToggleState} isMoodleCourse={isMoodleSession || false} />
    ),
  },
];

export const getSessionsScheduledConfig = () => [
  {
    title: i18next.t('tablesLabels.id'),
    field: 'id',
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.trainingModule'),
    field: 'trainingModuleLabel',
  },
  {
    title: i18next.t('tablesLabels.trainingPart'),
    field: 'trainingPartLabel',
  },
  {
    title: i18next.t('tablesLabels.startDate'),
    field: 'startDate',
    Component: value => (value ? <DateFormatter value={value} /> : '—'),
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.expiryDate'),
    field: 'expirationDate',
    Component: value => (value ? <DateFormatter value={value} /> : i18next.t('courseDetails.incomplete')),
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.jobTask'),
    field: 'jobTask',
    isSorting: true,
  },
];

export const getSessionsPastConfig = helpers => [
  {
    title: i18next.t('tablesLabels.id'),
    field: 'id',
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.trainingModule'),
    field: 'trainingModuleLabel',
  },
  {
    title: i18next.t('tablesLabels.trainingPart'),
    field: 'trainingPartLabel',
  },
  {
    title: i18next.t('tablesLabels.performedDate'),
    field: 'completionDate',
    Component: value => (value ? <DateFormatter value={value} /> : '—'),
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.expiryDate'),
    field: 'expirationDate',
    Component: value => (value == null ? i18next.t('commonValues.notAvailable') : <DateFormatter value={value} />),
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.status'),
    field: 'statusLabel',
    Component: value => value || i18next.t('commonValues.notAvailable'),
  },
  {
    title: i18next.t('trainingSessions.evidence'),
    field: 'evidenceParams',
    alignment: 'center',
    Component: value => formatEvidenceCell(value, helpers),
  },
];

export { formatTrainingModuleLabel, formatTrainingPartLabel };
